function createInfiniteCarousel (containerSelector, direction, options = {}) {
  const {
    speed = 60,
    pauseWhenHidden = true
  } = options

  const containers = document.querySelectorAll(containerSelector)
  if (!containers.length) return () => {}

  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
  if (reducedMotion) return () => {}

  const destroyFns = []

  containers.forEach((container) => {
    const slide = container.querySelector('.slide')
    if (!slide) return

    container.style.willChange = 'transform'

    let slideWidth = 0
    let position = 0
    let rafId = null
    let lastTime = 0

    let clone = container.querySelector(':scope > .slide.clone')
    if (!clone) {
      clone = slide.cloneNode(true)
      clone.classList.add('clone')
      container.appendChild(clone)
    }

    const measure = () => {
      slideWidth = slide.getBoundingClientRect().width
      if (!slideWidth) return

      position = direction === 'right' ? -slideWidth : 0
      container.style.transform = `translate3d(${position}px, 0, 0)`
    }

    const step = (time) => {
      if (!lastTime) lastTime = time
      const delta = speed * ((time - lastTime) / 1000)
      lastTime = time

      if (direction === 'left') {
        position -= delta
        if (Math.abs(position) >= slideWidth) position = 0
      } else {
        position += delta
        if (position >= 0) position = -slideWidth
      }

      container.style.transform = `translate3d(${position}px, 0, 0)`
      rafId = requestAnimationFrame(step)
    }

    const start = () => {
      cancelAnimationFrame(rafId)
      lastTime = 0
      rafId = requestAnimationFrame(step)
    }

    const stop = () => {
      cancelAnimationFrame(rafId)
      rafId = null
    }

    let resizeTimeout = null
    const onResize = () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => {
        measure()
      }, 150)
    }

    const onVisibility = () => {
      if (!pauseWhenHidden) return
      if (document.hidden) stop()
      else start()
    }

    window.addEventListener('resize', onResize)
    document.addEventListener('visibilitychange', onVisibility)

    measure()
    start()

    destroyFns.push(() => {
      stop()
      clearTimeout(resizeTimeout)
      window.removeEventListener('resize', onResize)
      document.removeEventListener('visibilitychange', onVisibility)
    })
  })

  return () => destroyFns.forEach((destroy) => destroy())
}

let destroyAnnouncementCarousel = null
let destroyHeaderController = null
let destroyMenuController = null
let destroyIndexPopupController = null
const COLLECTION_VARIANT_SOURCE_CACHE = new Map()
const productCardVariantControllers = new WeakMap()

const buildProductCardVariantUrl = (baseUrl, variantId) => {
  if (!baseUrl || !variantId) return baseUrl || ''

  const nextUrl = new URL(baseUrl, window.location.origin)
  nextUrl.searchParams.set('variant', variantId)

  return `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`
}

class ProductCardVariantController {
  constructor (card) {
    this.isReady = false
    this.card = card
    this.formHost = card.querySelector('[data-product-card-form]')
    this.form = this.formHost?.querySelector('form')

    if (!this.formHost || !this.form) return

    this.baseProductUrl = this.card.dataset.productCardBaseUrl || ''
    this.productLinks = Array.from(this.card.querySelectorAll('[data-product-card-link]'))
    this.hiddenVariantInput = this.form.querySelector('[name="id"]')
    this.image = this.card.querySelector('.product-card-img-wrapper img')
    this.baseImageSrc = this.image?.getAttribute('src') || ''
    this.baseImageAlt = this.image?.getAttribute('alt') || ''
    this.comparePrice = this.card.querySelector('[data-product-card-price-compare]')
    this.finalPrice = this.card.querySelector('[data-product-card-price-final]')
    this.saleBadge = this.card.querySelector('[data-product-card-sale-badge]')
    this.soldOutBadge = this.card.querySelector('[data-product-card-sold-out-badge]')
    this.atcButton = this.card.querySelector('[data-product-card-atc]')
    this.swatches = Array.from(this.card.querySelectorAll('[data-product-card-swatch]'))
    this.selects = Array.from(this.card.querySelectorAll('[data-product-card-select]'))
    this.useHoverSwatchSelection = window.matchMedia?.('(hover: hover) and (pointer: fine)')?.matches ?? false

    this.optionCount = Number(this.formHost.dataset.optionCount || 0)
    this.colorOptionIndex = this.formHost.dataset.colorOptionIndex === undefined
      ? null
      : Number(this.formHost.dataset.colorOptionIndex)

    this.variants = this.parseVariants()
    if (!this.variants.length) return

    this.currentVariant = this.findInitialVariant()
    this.currentOptions = this.currentVariant?.options?.slice() || this.readOptionsFromControls()

    this.syncControlsFromVariant(this.currentVariant)
    this.syncCardState(this.currentVariant)
    this.card.dataset.productCardReady = 'true'
    this.isReady = true
  }

  parseVariants () {
    try {
      return JSON.parse(this.formHost.dataset.variants || '[]')
    } catch (error) {
      console.error('Unable to parse product card variants JSON.', error)
      return []
    }
  }

  findInitialVariant () {
    const initialId = Number(this.hiddenVariantInput?.value || 0)
    return this.variants.find(variant => variant.id === initialId) || this.variants[0] || null
  }

  readOptionsFromControls () {
    const fallbackOptions = this.currentVariant?.options?.slice() || new Array(this.optionCount).fill('')

    this.selects.forEach((select) => {
      fallbackOptions[Number(select.dataset.optionIndex)] = select.value
    })

    this.swatches.forEach((swatch) => {
      if (swatch.getAttribute('aria-pressed') === 'true') {
        fallbackOptions[Number(swatch.dataset.optionIndex)] = swatch.dataset.optionValue
      }
    })

    return fallbackOptions
  }

  findVariantByOptions (options) {
    return this.variants.find((variant) =>
      JSON.stringify(variant.options) === JSON.stringify(options)
    ) || null
  }

  findFallbackVariant (options, changedIndex) {
    const changedValue = options[changedIndex]
    const currentColorValue = this.colorOptionIndex !== null ? options[this.colorOptionIndex] : null

    const matchingChangedValue = this.variants.filter((variant) => variant.options[changedIndex] === changedValue)

    if (changedIndex === this.colorOptionIndex) {
      return matchingChangedValue.find((variant) => variant.available) || matchingChangedValue[0] || null
    }

    if (this.colorOptionIndex !== null) {
      const sameColorVariants = matchingChangedValue.filter((variant) => variant.options[this.colorOptionIndex] === currentColorValue)
      return sameColorVariants.find((variant) => variant.available) ||
        sameColorVariants[0] ||
        matchingChangedValue.find((variant) => variant.available) ||
        matchingChangedValue[0] ||
        null
    }

    return matchingChangedValue.find((variant) => variant.available) || matchingChangedValue[0] || null
  }

  onSwatchInteract (swatch) {
    const optionIndex = Number(swatch.dataset.optionIndex)
    const nextOptions = this.readOptionsFromControls()
    nextOptions[optionIndex] = swatch.dataset.optionValue

    const nextVariant = this.findVariantByOptions(nextOptions) || this.findFallbackVariant(nextOptions, optionIndex)
    this.commitVariant(nextVariant, nextOptions)
  }

  onSelectChange (select) {
    const optionIndex = Number(select.dataset.optionIndex)
    const nextOptions = this.readOptionsFromControls()
    nextOptions[optionIndex] = select.value

    const nextVariant = this.findVariantByOptions(nextOptions) || this.findFallbackVariant(nextOptions, optionIndex)
    this.commitVariant(nextVariant, nextOptions)
  }

  commitVariant (variant, requestedOptions = []) {
    // When there is a valid match we synchronize every control to that variant,
    // which keeps swatches, selects and the hidden variant id in one source of truth.
    if (!variant) {
      this.currentOptions = requestedOptions.slice()
      this.hiddenVariantInput.value = ''
      this.updateImage(this.currentVariant)
      this.updateButtonState(null)
      return
    }

    this.currentVariant = variant
    this.currentOptions = variant.options.slice()
    this.hiddenVariantInput.value = variant.id

    this.syncControlsFromVariant(variant)
    this.syncCardState(variant)
  }

  syncControlsFromVariant (variant) {
    if (!variant) return

    this.selects.forEach((select) => {
      const optionIndex = Number(select.dataset.optionIndex)
      select.value = variant.options[optionIndex] || select.value
    })

    this.swatches.forEach((swatch) => {
      const isSelected = variant.options[Number(swatch.dataset.optionIndex)] === swatch.dataset.optionValue
      swatch.setAttribute('aria-pressed', isSelected ? 'true' : 'false')
      swatch.classList.toggle('border-dark', isSelected)
      swatch.classList.toggle('border-white', !isSelected)
      swatch.classList.toggle('shadow-sm', isSelected)
    })
  }

  syncCardState (variant) {
    if (!variant) return

    this.updateImage(variant)
    this.updateLinks(variant)
    this.updatePricing(variant)
    this.updateButtonState(variant)
  }

  updateLinks (variant) {
    if (!variant?.id || !this.productLinks.length) return

    this.productLinks.forEach((link) => {
      const rawBaseUrl = this.baseProductUrl || link.getAttribute('href') || ''
      if (!rawBaseUrl) return

      link.href = buildProductCardVariantUrl(rawBaseUrl, variant.id)
    })
  }

  updateImage (variant) {
    if (!this.image) return

    const nextSrc = variant?.featured_image || this.baseImageSrc
    if (nextSrc) {
      this.image.src = nextSrc
    }

    if (variant?.featured_image_alt) {
      this.image.alt = variant.featured_image_alt
    } else if (this.baseImageAlt) {
      this.image.alt = this.baseImageAlt
    }
  }

  updatePricing (variant) {
    if (!this.finalPrice || !variant) return

    const compareAtPrice = Number(variant.compare_at_price || 0)
    const finalPrice = Number(variant.price || 0)
    const isOnSale = compareAtPrice > finalPrice

    if (this.comparePrice) {
      if (isOnSale) {
        this.comparePrice.textContent = window.Shopify.formatMoney(compareAtPrice)
        this.comparePrice.removeAttribute('hidden')
      } else {
        this.comparePrice.textContent = ''
        this.comparePrice.setAttribute('hidden', 'hidden')
      }
    }

    if (isOnSale) {
      this.finalPrice.innerHTML = `<span class="visually-hidden">${window.theme.locales.product.sale_price}</span>${window.Shopify.formatMoney(finalPrice)}`
      this.finalPrice.classList.add('product-price-final-sale')
    } else {
      this.finalPrice.innerHTML = window.Shopify.formatMoney(finalPrice)
      this.finalPrice.classList.remove('product-price-final-sale')
    }

    if (this.saleBadge) {
      if (isOnSale) {
        this.saleBadge.removeAttribute('hidden')

        if (this.saleBadge.dataset.discountType === 'percentage') {
          const savingsPercentage = Math.round(((compareAtPrice - finalPrice) * 100) / compareAtPrice)
          this.saleBadge.innerHTML = `<span class="visually-hidden">${window.theme.locales.product.save}</span>-${savingsPercentage}%`
        } else {
          const savingsValue = window.Shopify.formatMoney(compareAtPrice - finalPrice)
          this.saleBadge.textContent = `${window.theme.locales.product.save} ${savingsValue}`
        }
      } else {
        this.saleBadge.textContent = ''
        this.saleBadge.setAttribute('hidden', 'hidden')
      }
    }

    if (this.soldOutBadge) {
      if (variant.available) {
        this.soldOutBadge.setAttribute('hidden', 'hidden')
      } else {
        this.soldOutBadge.removeAttribute('hidden')
      }
    }
  }

  updateButtonState (variant) {
    if (!this.atcButton) return

    if (!variant) {
      this.atcButton.setAttribute('disabled', 'disabled')
      this.atcButton.type = 'button'
      delete this.atcButton.dataset.productCardViewUrl
      this.atcButton.innerHTML = window.theme.locales.product.unavailable
      return
    }

    if (variant.available) {
      this.atcButton.removeAttribute('disabled')
      this.atcButton.type = 'submit'
      delete this.atcButton.dataset.productCardViewUrl
      this.atcButton.innerHTML = window.theme.locales.product.add_to_cart
      return
    }

    // Sold out cards should lead to the PDP so customers can use the notify-me flow.
    this.atcButton.removeAttribute('disabled')
    this.atcButton.type = 'button'
    this.atcButton.dataset.productCardViewUrl = buildProductCardVariantUrl(
      this.baseProductUrl || this.productLinks[0]?.getAttribute('href'),
      variant.id
    )
    this.atcButton.innerHTML = window.theme.locales.product.view_product
  }
}

const getProductCardVariantController = (card) => {
  if (!card) return null

  const existingController = productCardVariantControllers.get(card)
  if (existingController?.isReady) return existingController

  const controller = new ProductCardVariantController(card)
  if (!controller?.isReady) return null

  productCardVariantControllers.set(card, controller)
  return controller
}

const mountProductCardVariantControllers = (root = document) => {
  root.querySelectorAll('[data-product-card]').forEach((card) => {
    getProductCardVariantController(card)
  })
}

const mountDelegatedProductCardEvents = () => {
  if (document.documentElement.dataset.productCardDelegationBound === 'true') return

  document.documentElement.dataset.productCardDelegationBound = 'true'

  document.addEventListener('mouseover', (event) => {
    const swatch = event.target.closest('[data-product-card-swatch]')
    if (!swatch) return

    if (event.relatedTarget && swatch.contains(event.relatedTarget)) return

    const controller = getProductCardVariantController(swatch.closest('[data-product-card]'))
    if (!controller?.useHoverSwatchSelection) return

    controller.onSwatchInteract(swatch)
  })

  document.addEventListener('click', (event) => {
    const viewProductButton = event.target.closest('[data-product-card-view-url]')
    if (viewProductButton) {
      event.preventDefault()
      window.location.href = viewProductButton.dataset.productCardViewUrl
      return
    }

    const swatch = event.target.closest('[data-product-card-swatch]')
    if (!swatch) return

    const controller = getProductCardVariantController(swatch.closest('[data-product-card]'))
    if (!controller || controller.useHoverSwatchSelection) return

    controller.onSwatchInteract(swatch)
  })

  document.addEventListener('focusin', (event) => {
    const swatch = event.target.closest('[data-product-card-swatch]')
    if (!swatch) return

    const controller = getProductCardVariantController(swatch.closest('[data-product-card]'))
    if (!controller) return

    controller.onSwatchInteract(swatch)
  })

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-product-card-select]')
    if (!select) return

    const controller = getProductCardVariantController(select.closest('[data-product-card]'))
    if (!controller) return

    controller.onSelectChange(select)
  })
}

const mountAnnouncementCarousel = () => {
  destroyAnnouncementCarousel?.()
  destroyAnnouncementCarousel = createInfiniteCarousel('.announcement_bar_container', 'left', {
    speed: 70
  })
}

const parseVariantCollectionHtmlDocument = (html) => {
  const parser = new DOMParser()
  return parser.parseFromString(html, 'text/html')
}

const scrollVariantCollectionIntoView = () => {
  const collectionProducts = document.querySelector('.collection-products')
  if (!collectionProducts) return

  const navbarHeight = document.querySelector('[id*="__navbar"].sticky-top')?.clientHeight || 0
  collectionProducts.style.scrollMarginTop = `${navbarHeight}px`
  collectionProducts.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const escapeVariantCollectionHtml = (value = '') => String(value)
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#39;')

const parseVariantCollectionMoneyToCents = (value) => {
  const normalized = String(value ?? '').replaceAll(',', '').trim()
  if (!normalized) return null

  const parsed = Number.parseFloat(normalized)
  if (Number.isNaN(parsed)) return null

  return Math.round(parsed * 100)
}

const flattenVariantCollectionFilterValue = (rawValue) => {
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return []
  }

  if (Array.isArray(rawValue)) {
    return [...new Set(rawValue.flatMap(flattenVariantCollectionFilterValue))]
  }

  if (typeof rawValue === 'object') {
    if ('value' in rawValue) {
      return flattenVariantCollectionFilterValue(rawValue.value)
    }

    for (const key of ['handle', 'label', 'name', 'id']) {
      if (rawValue[key] !== undefined && rawValue[key] !== null && rawValue[key] !== '') {
        return flattenVariantCollectionFilterValue(rawValue[key])
      }
    }

    return [...new Set(Object.values(rawValue).flatMap(flattenVariantCollectionFilterValue))]
  }

  return [String(rawValue)]
}

const buildVariantCollectionPaginationParts = (currentPage, totalPages) => {
  const parts = []

  for (let page = 1; page <= totalPages; page += 1) {
    const isEdgePage = page === 1 || page === totalPages
    const isNearbyPage = Math.abs(page - currentPage) <= 2

    if (isEdgePage || isNearbyPage) {
      parts.push(page)
      continue
    }

    if (parts[parts.length - 1] !== 'ellipsis') {
      parts.push('ellipsis')
    }
  }

  return parts
}

class VariantCollectionMode {
  constructor (root) {
    this.root = root
    this.configNode = root.querySelector('[data-variant-collection-config]')
    if (!this.configNode) return

    this.config = JSON.parse(this.configNode.textContent)
    this.grid = root.querySelector('[data-variant-collection-grid]')
    this.count = root.querySelector('[data-variant-collection-count]')
    this.filterTriggerCount = root.querySelector('[data-variant-collection-filter-count]')
    this.pagination = root.querySelector('[data-variant-collection-pagination]')
    this.sortWrapper = root.querySelector('[data-variant-collection-sort]')
    this.filterForm = document.querySelector('[data-variant-collection-filter-form]')
    this.filterGroups = document.querySelector('[data-variant-collection-filter-groups]')
    this.viewResultsButton = document.querySelector('[data-variant-collection-view-results]')
    this.clearAllButton = document.querySelector('[data-variant-collection-clear-all]')
    this.collator = new Intl.Collator(document.documentElement.lang || 'en', {
      sensitivity: 'base',
      numeric: true
    })

    this.filtersMeta = []
    this.renderToken = 0
    this.lastVisibleRecords = []
    this.boundHandlePopstate = this.handlePopstate.bind(this)

    this.bindEvents()
    this.renderFromUrl({ scroll: false })
  }

  bindEvents () {
    this.sortWrapper?.addEventListener('change', async (event) => {
      const input = event.target.closest('input[name="sort_by"]')
      if (!input) return

      const params = this.getCleanUrlParams()
      params.set('sort_by', input.value)
      params.delete('variant_page')
      params.delete('page')

      this.pushUrl(params)
      await this.renderFromUrl({ scroll: true })
    })

    this.filterForm?.addEventListener('change', async (event) => {
      const target = event.target
      if (!(target instanceof HTMLInputElement)) return

      const params = this.getCleanUrlParams()
      const activeParams = new URLSearchParams(this.getCleanUrlParams())

      if (target.type === 'checkbox') {
        const currentValues = activeParams.getAll(target.name)
        activeParams.delete(target.name)

        const nextValues = target.checked
          ? [...currentValues, target.value]
          : currentValues.filter(value => value !== target.value)

        nextValues.forEach(value => activeParams.append(target.name, value))
      } else if (target.type === 'number') {
        if (target.value === '') {
          activeParams.delete(target.name)
        } else {
          activeParams.set(target.name, target.value)
        }
      }

      activeParams.delete('variant_page')
      activeParams.delete('page')

      for (const key of [...params.keys()]) {
        if (key.startsWith('filter.')) {
          params.delete(key)
        }
      }

      for (const [key, value] of activeParams.entries()) {
        if (key.startsWith('filter.')) {
          params.append(key, value)
        }
      }

      if (activeParams.get('sort_by')) {
        params.set('sort_by', activeParams.get('sort_by'))
      }

      this.pushUrl(params)
      await this.renderFromUrl({ scroll: true })
    })

    this.clearAllButton?.addEventListener('click', async () => {
      const params = this.getCleanUrlParams()

      for (const key of [...params.keys()]) {
        if (key.startsWith('filter.')) {
          params.delete(key)
        }
      }

      params.delete('variant_page')
      params.delete('page')

      this.pushUrl(params)
      await this.renderFromUrl({ scroll: true })
    })

    this.pagination?.addEventListener('click', async (event) => {
      const link = event.target.closest('[data-variant-page]')
      if (!link) return

      event.preventDefault()

      const params = this.getCleanUrlParams()
      params.set('variant_page', link.dataset.variantPage)
      params.delete('page')

      this.pushUrl(params)
      await this.renderFromUrl({ scroll: true })
    })

    window.addEventListener('popstate', this.boundHandlePopstate)
  }

  destroy () {
    window.removeEventListener('popstate', this.boundHandlePopstate)
  }

  handlePopstate () {
    this.renderFromUrl({ scroll: false })
  }

  getCleanUrlParams () {
    const params = new URLSearchParams(window.location.search)
    params.delete('page')
    return params
  }

  getRequestedSortBy (params = this.getCleanUrlParams()) {
    return params.get('sort_by') || this.config.initialSortBy || this.config.defaultSortBy || 'manual'
  }

  getSourceOrderKey (sortBy) {
    if (sortBy === 'manual' || sortBy === 'best-selling') {
      return sortBy
    }

    return this.config.defaultSortBy || this.config.initialSortBy || 'manual'
  }

  buildSourceRequestUrl (page, sourceOrderKey) {
    const url = new URL(window.location.pathname, window.location.origin)
    const params = new URLSearchParams(window.location.search)

    for (const key of [...params.keys()]) {
      if (key.startsWith('filter.') || key === 'variant_page' || key === 'page' || key === 'section_id' || key === 'sections') {
        params.delete(key)
      }
    }

    params.set('section_id', this.config.sourceSectionId)
    params.set('page', String(page))
    params.set('sort_by', sourceOrderKey || 'manual')

    url.search = params.toString()
    return url.toString()
  }

  async fetchSourcePage (page, sourceOrderKey) {
    const response = await fetch(this.buildSourceRequestUrl(page, sourceOrderKey))
    const html = await response.text()
    const documentNode = parseVariantCollectionHtmlDocument(html)
    const payloadNode = documentNode.querySelector('[data-variant-collection-source-payload]')

    if (!payloadNode) {
      throw new Error('Variant collection source payload was not found.')
    }

    return JSON.parse(payloadNode.textContent)
  }

  async getDataset (sortBy) {
    const sourceOrderKey = this.getSourceOrderKey(sortBy)
    const cacheKey = `${window.location.pathname}::${sourceOrderKey}::${Number(Boolean(this.config.showSoldOutVariants))}`

    if (!COLLECTION_VARIANT_SOURCE_CACHE.has(cacheKey)) {
      const datasetPromise = (async () => {
        const firstPayload = await this.fetchSourcePage(1, sourceOrderKey)
        const totalPages = Number(firstPayload.collection?.pages || 1)
        const pagePromises = []

        for (let page = 2; page <= totalPages; page += 1) {
          pagePromises.push(this.fetchSourcePage(page, sourceOrderKey))
        }

        const nextPagesPayloads = await Promise.all(pagePromises)
        const rawVariants = [firstPayload, ...nextPagesPayloads].flatMap(payload => payload.variants || [])

        return {
          filters: firstPayload.filters || [],
          records: rawVariants.map((record) => ({
            ...record,
            price: Number(record.price || 0),
            compare_at_price: Number(record.compare_at_price || 0),
            created_at: Number(record.created_at || 0),
            base_position: Number(record.base_position || 0),
            variant_position: Number(record.variant_position || 0),
            _facetCache: {}
          }))
        }
      })()

      COLLECTION_VARIANT_SOURCE_CACHE.set(cacheKey, datasetPromise)
      datasetPromise.catch(() => COLLECTION_VARIANT_SOURCE_CACHE.delete(cacheKey))
    }

    return COLLECTION_VARIANT_SOURCE_CACHE.get(cacheKey)
  }

  getRecordFilterValues (record, filter) {
    if (record._facetCache[filter.param_name]) {
      return record._facetCache[filter.param_name]
    }

    let values = []

    switch (filter.source) {
      case 'vendor':
        values = flattenVariantCollectionFilterValue(record.vendor)
        break
      case 'type':
        values = flattenVariantCollectionFilterValue(record.type)
        break
      case 'tag':
        values = flattenVariantCollectionFilterValue(record.tags)
        break
      case 'availability':
        values = [String(Boolean(record.available))]
        break
      case 'option':
        values = flattenVariantCollectionFilterValue(record.options_by_handle?.[filter.option_handle])
        break
      case 'metafield':
        if (filter.scope === 'p') {
          values = flattenVariantCollectionFilterValue(record.product_metafields?.[filter.param_name])
        } else {
          values = flattenVariantCollectionFilterValue(record.variant_metafields?.[filter.param_name])
        }
        break
      default:
        values = []
        break
    }

    record._facetCache[filter.param_name] = [...new Set(values.map(String))]
    return record._facetCache[filter.param_name]
  }

  parseActiveState (params) {
    const state = {}

    this.filtersMeta.forEach((filter) => {
      if (filter.type === 'price_range') {
        const rawMin = params.get(filter.min_param_name) || ''
        const rawMax = params.get(filter.max_param_name) || ''

        if (rawMin !== '' || rawMax !== '') {
          state[filter.param_name] = {
            type: filter.type,
            rawMin,
            rawMax,
            minCents: parseVariantCollectionMoneyToCents(rawMin),
            maxCents: parseVariantCollectionMoneyToCents(rawMax)
          }
        }

        return
      }

      const values = params.getAll(filter.param_name).map(String)
      if (values.length) {
        state[filter.param_name] = {
          type: filter.type,
          values
        }
      }
    })

    return state
  }

  getActiveFilterCount (activeState) {
    return Object.values(activeState).reduce((count, entry) => {
      if (entry.type === 'price_range') {
        return count + 1
      }

      return count + entry.values.length
    }, 0)
  }

  recordMatchesFilterValues (record, filter, selectedValues = []) {
    if (!selectedValues.length) return true

    const recordValues = this.getRecordFilterValues(record, filter)
    if (!recordValues.length) return false

    const operator = String(filter.operator || 'OR').toUpperCase()

    if (operator === 'AND') {
      return selectedValues.every(value => recordValues.includes(String(value)))
    }

    return selectedValues.some(value => recordValues.includes(String(value)))
  }

  recordMatchesPriceFilter (record, activePriceState) {
    if (!activePriceState) return true

    if (activePriceState.minCents !== null && record.price < activePriceState.minCents) {
      return false
    }

    if (activePriceState.maxCents !== null && record.price > activePriceState.maxCents) {
      return false
    }

    return true
  }

  recordMatchesAllFilters (record, activeState, skipParamName = null) {
    return this.filtersMeta.every((filter) => {
      if (filter.param_name === skipParamName) {
        return true
      }

      const activeEntry = activeState[filter.param_name]
      if (!activeEntry) {
        return true
      }

      if (filter.type === 'price_range') {
        return this.recordMatchesPriceFilter(record, activeEntry)
      }

      return this.recordMatchesFilterValues(record, filter, activeEntry.values)
    })
  }

  sortRecords (records, sortBy) {
    const stableCompare = (recordA, recordB) => {
      if (recordA.base_position !== recordB.base_position) {
        return recordA.base_position - recordB.base_position
      }

      return recordA.variant_position - recordB.variant_position
    }

    const sortedRecords = [...records]

    switch (sortBy) {
      case 'best-selling':
      case 'manual':
        return sortedRecords.sort(stableCompare)
      case 'title-ascending':
        return sortedRecords.sort((recordA, recordB) =>
          this.collator.compare(recordA.visible_title, recordB.visible_title) || stableCompare(recordA, recordB)
        )
      case 'title-descending':
        return sortedRecords.sort((recordA, recordB) =>
          this.collator.compare(recordB.visible_title, recordA.visible_title) || stableCompare(recordA, recordB)
        )
      case 'price-ascending':
        return sortedRecords.sort((recordA, recordB) =>
          (recordA.price - recordB.price) || stableCompare(recordA, recordB)
        )
      case 'price-descending':
        return sortedRecords.sort((recordA, recordB) =>
          (recordB.price - recordA.price) || stableCompare(recordA, recordB)
        )
      case 'created-ascending':
        return sortedRecords.sort((recordA, recordB) =>
          (recordA.created_at - recordB.created_at) || stableCompare(recordA, recordB)
        )
      case 'created-descending':
        return sortedRecords.sort((recordA, recordB) =>
          (recordB.created_at - recordA.created_at) || stableCompare(recordA, recordB)
        )
      default:
        return sortedRecords.sort(stableCompare)
    }
  }

  computeFilterCounts (records, activeState) {
    const counts = {}

    this.filtersMeta.forEach((filter) => {
      if (filter.type === 'price_range') return

      const baseRecords = records.filter(record => this.recordMatchesAllFilters(record, activeState, filter.param_name))
      const currentActiveValues = activeState[filter.param_name]?.values || []
      const valueDescriptors = filter.type === 'boolean'
        ? [filter.true_value, filter.false_value]
        : (filter.values || [])

      counts[filter.param_name] = {}

      valueDescriptors.forEach((valueDescriptor) => {
        const valueKey = String(valueDescriptor.value)
        const nextValues = currentActiveValues.includes(valueKey)
          ? [...currentActiveValues]
          : [...new Set([...currentActiveValues, valueKey])]

        counts[filter.param_name][valueKey] = baseRecords.reduce((total, record) => (
          this.recordMatchesFilterValues(record, filter, nextValues) ? total + 1 : total
        ), 0)
      })
    })

    return counts
  }

  renderFilterGroups (counts, activeState) {
    return this.filtersMeta.map((filter, filterIndex) => {
      const collapseId = `variant-filters-collapse-${filterIndex}`
      const buttonId = `variant-filters-collapse-btn-${filterIndex}`

      if (filter.type === 'price_range') {
        const activePrice = activeState[filter.param_name] || {}

        return `
          <div class="collapse-wrapper" role="listitem">
            <button
              id="${buttonId}"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#${collapseId}"
              aria-expanded="true"
              aria-controls="${collapseId}"
            >
              ${escapeVariantCollectionHtml(filter.label)}
              <span aria-hidden="true">⌄</span>
            </button>
            <div id="${collapseId}" class="collapse show">
              <div class="collapse-inner">
                <div class="filter-price-group row mx-n2">
                  <div class="col-9 px-2">
                    <div class="input-group">
                      <span class="input-group-text px-3">${escapeVariantCollectionHtml(filter.currency_symbol || '')}</span>
                      <input
                        class="form-control"
                        name="${escapeVariantCollectionHtml(filter.min_param_name)}"
                        value="${escapeVariantCollectionHtml(activePrice.rawMin || '')}"
                        type="number"
                        min="0"
                        placeholder="${escapeVariantCollectionHtml(this.config.translations.priceFrom)}"
                        aria-label="${escapeVariantCollectionHtml(this.config.translations.priceFrom)}"
                      >
                    </div>
                  </div>
                  <div class="col-9 px-2">
                    <div class="input-group">
                      <span class="input-group-text px-3">${escapeVariantCollectionHtml(filter.currency_symbol || '')}</span>
                      <input
                        class="form-control"
                        name="${escapeVariantCollectionHtml(filter.max_param_name)}"
                        value="${escapeVariantCollectionHtml(activePrice.rawMax || '')}"
                        type="number"
                        min="0"
                        placeholder="${escapeVariantCollectionHtml(this.config.translations.priceTo)}"
                        aria-label="${escapeVariantCollectionHtml(this.config.translations.priceTo)}"
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        `
      }

      const values = filter.type === 'boolean'
        ? [filter.true_value, filter.false_value]
        : (filter.values || [])

      const valuesMarkup = values.map((valueDescriptor, valueIndex) => {
        const valueKey = String(valueDescriptor.value)
        const count = counts[filter.param_name]?.[valueKey] || 0
        const isChecked = Boolean(activeState[filter.param_name]?.values?.includes(valueKey))
        const isDisabled = count === 0 && !isChecked
        const inputId = `variant-filter-${filterIndex}-${valueIndex}`

        return `
          <li class="form-check">
            <input
              type="checkbox"
              id="${inputId}"
              class="form-check-input"
              name="${escapeVariantCollectionHtml(filter.param_name)}"
              value="${escapeVariantCollectionHtml(valueKey)}"
              ${isChecked ? 'checked' : ''}
              ${isDisabled ? 'disabled' : ''}
            >
            <label class="form-check-label" for="${inputId}">
              <div class="d-flex">
                <span>${escapeVariantCollectionHtml(valueDescriptor.label)}</span>
                <span class="ms-2">(${count})</span>
              </div>
            </label>
          </li>
        `
      }).join('')

      return `
        <div class="collapse-wrapper" role="listitem">
          <button
            id="${buttonId}"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#${collapseId}"
            aria-expanded="true"
            aria-controls="${collapseId}"
          >
            ${escapeVariantCollectionHtml(filter.label)}
            <span aria-hidden="true">⌄</span>
          </button>
          <div id="${collapseId}" class="collapse show">
            <div class="collapse-inner">
              <ul class="list-unstyled mb-0" aria-labelledby="${buttonId}">
                ${valuesMarkup}
              </ul>
            </div>
          </div>
        </div>
      `
    }).join('')
  }

  renderPagination (currentPage, totalPages) {
    if (!this.pagination) return

    if (totalPages <= 1) {
      this.pagination.innerHTML = ''
      return
    }

    const params = this.getCleanUrlParams()
    const pageParts = buildVariantCollectionPaginationParts(currentPage, totalPages)

    const buildPageUrl = (pageNumber) => {
      const nextParams = new URLSearchParams(params)
      nextParams.set('variant_page', String(pageNumber))
      return `${window.location.pathname}?${nextParams.toString()}`
    }

    const partsMarkup = pageParts.map((part) => {
      if (part === 'ellipsis') {
        return `
          <li class="page-item disabled" aria-hidden="true">
            <span class="page-link">&hellip;</span>
          </li>
        `
      }

      if (part === currentPage) {
        return `
          <li class="page-item active" aria-current="page">
            <span class="page-link">${part}</span>
          </li>
        `
      }

      return `
        <li class="page-item">
          <a class="page-link" href="${buildPageUrl(part)}" data-variant-page="${part}">
            ${part}
          </a>
        </li>
      `
    }).join('')

    this.pagination.innerHTML = `
      <nav aria-label="${escapeVariantCollectionHtml(this.config.translations.paginationLabel)}">
        <ul class="pagination justify-content-center my-6">
          ${currentPage > 1 ? `
            <li class="page-item">
              <a class="page-link" href="${buildPageUrl(currentPage - 1)}" data-variant-page="${currentPage - 1}">
                <span aria-hidden="true">&laquo;</span>
              </a>
            </li>
          ` : ''}
          ${partsMarkup}
          ${currentPage < totalPages ? `
            <li class="page-item">
              <a class="page-link" href="${buildPageUrl(currentPage + 1)}" data-variant-page="${currentPage + 1}">
                <span aria-hidden="true">&raquo;</span>
              </a>
            </li>
          ` : ''}
        </ul>
      </nav>
    `
  }

  formatCount (count, singularTemplate, pluralTemplate) {
    const pluralRules = new Intl.PluralRules(document.documentElement.lang || 'en')
    const template = pluralRules.select(count) === 'one' ? singularTemplate : pluralTemplate
    return template.replaceAll('[count]', String(count))
  }

  renderGrid (records) {
    if (!this.grid) return

    this.grid.classList.remove('is-loading')

    if (!records.length) {
      this.grid.innerHTML = `
        <div class="alert alert-primary mb-0">
          ${this.config.translations.emptyHtml}
        </div>
      `
      return
    }

    const itemsMarkup = records.map((record) => `
      <div class="${this.config.itemClasses}" role="listitem">
        ${record.html}
      </div>
    `).join('')

    this.grid.innerHTML = `
      <div class="${this.config.rowClasses}" role="list">
        ${itemsMarkup}
      </div>
    `
  }

  updateControls (counts, totalCount, currentPage, totalPages, activeState, sortBy) {
    if (this.count) {
      this.count.textContent = this.formatCount(
        totalCount,
        this.config.translations.countOne,
        this.config.translations.countOther
      )
    }

    const activeFilterCount = this.getActiveFilterCount(activeState)

    if (this.filterTriggerCount) {
      this.filterTriggerCount.textContent = activeFilterCount > 0 ? ` (${activeFilterCount})` : ''
    }

    if (this.filterGroups) {
      this.filterGroups.innerHTML = this.renderFilterGroups(counts, activeState)
    }

    if (this.viewResultsButton) {
      this.viewResultsButton.textContent = this.formatCount(
        totalCount,
        this.config.translations.viewResultsOne,
        this.config.translations.viewResultsOther
      )
    }

    if (this.clearAllButton) {
      this.clearAllButton.hidden = activeFilterCount === 0
    }

    if (this.sortWrapper) {
      this.sortWrapper.querySelectorAll('input[name="sort_by"]').forEach((input) => {
        input.checked = input.value === sortBy
      })
    }

    this.renderPagination(currentPage, totalPages)
  }

  pushUrl (params) {
    const query = params.toString()
    const url = query ? `${window.location.pathname}?${query}` : window.location.pathname
    window.history.pushState({}, '', url)
  }

  async renderFromUrl ({ scroll }) {
    try {
      const token = ++this.renderToken
      const params = this.getCleanUrlParams()
      const requestedSortBy = this.getRequestedSortBy(params)

      this.grid?.classList.add('is-loading')

      const dataset = await this.getDataset(requestedSortBy)
      if (token !== this.renderToken) return

      this.filtersMeta = dataset.filters || []
      const activeState = this.parseActiveState(params)
      const filteredRecords = dataset.records.filter((record) => this.recordMatchesAllFilters(record, activeState))
      const sortedRecords = this.sortRecords(filteredRecords, requestedSortBy)

      const totalCount = sortedRecords.length
      const totalPages = Math.max(1, Math.ceil(totalCount / this.config.limit))
      const requestedPage = Math.max(1, Number.parseInt(params.get('variant_page') || '1', 10) || 1)
      const currentPage = Math.min(requestedPage, totalPages)
      const startIndex = (currentPage - 1) * this.config.limit
      const visibleRecords = sortedRecords.slice(startIndex, startIndex + this.config.limit)

      this.lastVisibleRecords = visibleRecords

      if (requestedPage !== currentPage) {
        const correctedParams = this.getCleanUrlParams()
        correctedParams.set('variant_page', String(currentPage))
        const query = correctedParams.toString()
        const url = query ? `${window.location.pathname}?${query}` : window.location.pathname
        window.history.replaceState({}, '', url)
      }

      const counts = this.computeFilterCounts(dataset.records, activeState)

      this.renderGrid(visibleRecords)
      this.updateControls(counts, totalCount, currentPage, totalPages, activeState, requestedSortBy)

      window.dispatchEvent(new CustomEvent('kt.collection.loaded'))

      if (scroll) {
        scrollVariantCollectionIntoView()
      }
    } catch (error) {
      console.error('Variant collection mode failed to render.', error)
      this.grid?.classList.remove('is-loading')

      if (this.grid) {
        this.grid.innerHTML = `
          <div class="alert alert-danger mb-0">
            Unable to load the collection.
          </div>
        `
      }
    }
  }
}

const mountVariantCollectionMode = () => {
  const variantCollectionRoot = document.querySelector('[data-variant-collection-root]')
  if (!variantCollectionRoot) {
    window.__ktVariantCollectionMode?.destroy?.()
    window.__ktVariantCollectionMode = null
    return
  }

  window.__ktVariantCollectionMode?.destroy?.()
  window.__ktVariantCollectionMode = new VariantCollectionMode(variantCollectionRoot)
}

const readMargaritaCookie = (name) => {
  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : ''
}

const writeMargaritaCookie = (name, value, maxAgeSeconds) => {
  const expiresAt = new Date()
  expiresAt.setTime(expiresAt.getTime() + maxAgeSeconds * 1000)
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expiresAt.toUTCString()}; path=/; SameSite=Lax`
}

const mountIndexPopup = () => {
  destroyIndexPopupController?.()
  destroyIndexPopupController = null

  const root = document.querySelector('[data-mc-index-popup-root]')
  if (!root) return

  const configNode = root.querySelector('[data-mc-index-popup-config]')
  const modalElement = root.querySelector('[data-mc-index-popup-modal]')
  if (!configNode || !modalElement || !window.bootstrap?.Modal) return

  let config = null

  try {
    config = JSON.parse(configNode.textContent || '{}')
  } catch (error) {
    console.error('Unable to parse index popup configuration.', error)
    return
  }

  const modal = window.bootstrap.Modal.getOrCreateInstance(modalElement)
  const hasSuccessState = Boolean(root.querySelector('[data-mc-index-popup-success="true"]'))
  const isDesignMode = Boolean(config.designMode || root.dataset.designMode === 'true')
  const showInThemeEditor = Boolean(config.showInThemeEditor)
  const cookieValue = String(config.cookieValue)
  const dismissalCookieName = config.dismissalCookieName || 'mc_index_popup_dismissed'
  const subscriptionCookieName = config.subscriptionCookieName || config.cookieName || 'mc_index_popup_subscribed'
  const dismissalCookieMaxAge = Number(config.dismissalCookieHours || 24) * 60 * 60
  const subscriptionCookieMaxAge = Number(config.subscriptionCookieYears || config.cookieYears || 100) * 365 * 24 * 60 * 60
  const hasDismissalCookie = readMargaritaCookie(dismissalCookieName) === cookieValue
  const hasSubscriptionCookie = readMargaritaCookie(subscriptionCookieName) === cookieValue

  const handleHidden = () => {
    if (isDesignMode) return
    writeMargaritaCookie(dismissalCookieName, cookieValue, dismissalCookieMaxAge)
  }

  modalElement.addEventListener('hidden.bs.modal', handleHidden)

  if (hasSuccessState) {
    if (!isDesignMode) {
      writeMargaritaCookie(subscriptionCookieName, cookieValue, subscriptionCookieMaxAge)
    }
    if (!isDesignMode || showInThemeEditor) {
      modal.show()
    }
  } else if (isDesignMode) {
    if (showInThemeEditor) {
      modal.show()
    }
  } else if (!hasSubscriptionCookie && !hasDismissalCookie) {
    modal.show()
  }

  destroyIndexPopupController = () => {
    modalElement.removeEventListener('hidden.bs.modal', handleHidden)
    modal.dispose()
  }
}

const mountMargaritaHeader = () => {
  destroyHeaderController?.()
  destroyHeaderController = null

  const header = document.querySelector('[data-mc-header]')
  if (!header) return
  const headerBar = header.querySelector('.mc-header__bar')
  if (!headerBar) return

  const headerGroup = header.closest('#header-group') || document.querySelector('#header-group')
  const announcement = headerGroup?.querySelector('.announcement_bar')
  const isOverlayHeader = header.dataset.isOverlay === 'true'

  let topObserver = null
  let resizeObserver = null
  let releaseTransitionFrame = null
  let scrollTimeout = null
  let topThreshold = 0
  let revealThreshold = 0
  let isFixedZone = false
  let isHeaderVisible = false
  let lastScroll = Math.max(window.scrollY, 0)
  let scrollDirection = 0
  let directionStartScroll = lastScroll

  const minDirectionDelta = 4
  const revealDistance = 28
  const hideDistance = 16
  const scrollThrottleMs = 20

  let sentinel = document.querySelector('[data-mc-header-sentinel]')
  if (!sentinel) {
    sentinel = document.createElement('div')
    sentinel.dataset.mcHeaderSentinel = 'true'
    sentinel.setAttribute('aria-hidden', 'true')
    sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;'
    document.body.prepend(sentinel)
  }

  const suspendTransitions = () => {
    header.classList.add('mc-header--no-transition')
    if (releaseTransitionFrame) {
      window.cancelAnimationFrame(releaseTransitionFrame)
    }

    releaseTransitionFrame = window.requestAnimationFrame(() => {
      releaseTransitionFrame = window.requestAnimationFrame(() => {
        header.classList.remove('mc-header--no-transition')
      })
    })
  }

  const measure = () => {
    const announcementHeight = announcement?.offsetHeight || 0
    const headerHeight = headerBar.offsetHeight || 0

    topThreshold = announcementHeight + headerHeight
    revealThreshold = topThreshold + 24

    document.documentElement.style.setProperty('--mc-announcement-height', `${announcementHeight}px`)
    document.documentElement.style.setProperty('--mc-header-height', `${headerHeight}px`)
    header.style.setProperty('--mc-announcement-height', `${announcementHeight}px`)
    header.style.setProperty('--mc-header-height', `${headerHeight}px`)
  }

  const resetScrollTracking = (scrollY = Math.max(window.scrollY, 0)) => {
    lastScroll = scrollY
    scrollDirection = 0
    directionStartScroll = scrollY
  }

  const hideFixedHeader = ({ instant = false } = {}) => {
    if (instant) suspendTransitions()
    header.classList.remove('mc-header--visible')
    isHeaderVisible = false
  }

  const showFixedHeader = () => {
    header.classList.add('mc-header--visible')
    isHeaderVisible = true
  }

  const applyTopState = () => {
    suspendTransitions()
    if (isOverlayHeader) header.classList.add('mc-header--home-top')
    else header.classList.remove('mc-header--home-top')

    header.classList.remove('mc-header--fixed')
    hideFixedHeader()
    resetScrollTracking()
  }

  const applyFixedState = () => {
    suspendTransitions()
    header.classList.remove('mc-header--home-top')
    header.classList.add('mc-header--fixed')
    hideFixedHeader()
    resetScrollTracking()
  }

  const setFixedZone = (shouldBeFixed) => {
    if (isFixedZone === shouldBeFixed) return

    isFixedZone = shouldBeFixed

    if (shouldBeFixed) {
      applyFixedState()
    } else {
      applyTopState()
    }
  }

  const rebuildTopObserver = () => {
    measure()
    topObserver?.disconnect()
    sentinel.style.top = `${topThreshold}px`

    if (!window.IntersectionObserver) {
      setFixedZone(Math.max(window.scrollY, 0) > topThreshold)
      return
    }

    topObserver = new window.IntersectionObserver((entries) => {
      const entry = entries[0]
      const currentScroll = Math.max(window.scrollY, 0)

      setFixedZone(currentScroll > topThreshold && !entry.isIntersecting)
    }, {
      root: null,
      threshold: 0,
      rootMargin: '0px'
    })

    topObserver.observe(sentinel)
    setFixedZone(Math.max(window.scrollY, 0) > topThreshold)
  }

  const processScroll = () => {
    scrollTimeout = null
    const currentScroll = Math.max(window.scrollY, 0)

    setFixedZone(currentScroll > topThreshold)

    if (!isFixedZone) {
      resetScrollTracking()
      return
    }

    const delta = currentScroll - lastScroll

    if (Math.abs(delta) < minDirectionDelta) {
      lastScroll = currentScroll
      return
    }

    const nextDirection = delta > 0 ? 1 : -1

    if (nextDirection !== scrollDirection) {
      scrollDirection = nextDirection
      directionStartScroll = lastScroll
    }

    const directionTravel = Math.abs(currentScroll - directionStartScroll)

    if (!isHeaderVisible && scrollDirection < 0 && currentScroll > revealThreshold && directionTravel >= revealDistance) {
      showFixedHeader()
    } else if (isHeaderVisible && scrollDirection > 0 && directionTravel >= hideDistance) {
      hideFixedHeader()
    }

    lastScroll = currentScroll
  }

  const onScroll = () => {
    if (scrollTimeout !== null) return
    scrollTimeout = window.setTimeout(processScroll, scrollThrottleMs)
  }

  const onResize = () => {
    rebuildTopObserver()
    processScroll()
  }

  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', onResize)

  if (window.ResizeObserver) {
    resizeObserver = new window.ResizeObserver(() => {
      rebuildTopObserver()
      processScroll()
    })

    resizeObserver.observe(headerBar)
    if (announcement) resizeObserver.observe(announcement)
  }

  rebuildTopObserver()
  processScroll()

  destroyHeaderController = () => {
    if (scrollTimeout !== null) {
      window.clearTimeout(scrollTimeout)
    }
    if (releaseTransitionFrame) {
      window.cancelAnimationFrame(releaseTransitionFrame)
    }

    topObserver?.disconnect()
    resizeObserver?.disconnect()
    window.removeEventListener('scroll', onScroll)
    window.removeEventListener('resize', onResize)
  }
}

const mountMargaritaMenuDrawer = () => {
  destroyMenuController?.()
  destroyMenuController = null

  const drawers = Array.from(document.querySelectorAll('[data-mc-menu-drawer]'))
  if (!drawers.length) return

  const desktopMedia = window.matchMedia('(min-width: 1200px)')
  const destroyFns = []

  drawers.forEach((drawer) => {
    const rootPanelId = drawer.dataset.rootPanel
    if (!rootPanelId) return

    const templateEntries = Array.from(drawer.querySelectorAll('template[data-menu-panel]')).map((template) => {
      return [template.dataset.menuPanel, template]
    })
    const panelTemplates = new Map(templateEntries)
    const desktopColumns = Array.from(drawer.querySelectorAll('[data-menu-desktop-column]'))
    const desktopShells = Array.from(drawer.querySelectorAll('[data-menu-desktop-shell]'))
    const mobileCarouselEl = drawer.querySelector('[data-menu-mobile-carousel]')
    const mobileInner = drawer.querySelector('[data-menu-mobile-inner]')
    const mobileRootSlideTemplate = drawer.querySelector('[data-menu-mobile-root-slide-template]')
    const mobileSlideTemplate = drawer.querySelector('[data-menu-mobile-slide-template]')

    let mobileStack = [rootPanelId]
    let mobileCarousel = null

    const clonePanel = (panelId) => {
      const template = panelTemplates.get(panelId)
      if (!template) return null

      const fragment = template.content.cloneNode(true)
      return fragment.firstElementChild
    }

    const cloneMobileSlide = (isRoot = false) => {
      const template = isRoot ? mobileRootSlideTemplate : mobileSlideTemplate
      if (!template) return null

      const fragment = template.content.cloneNode(true)
      return fragment.firstElementChild
    }

    const createMobileSlide = (panelId, isRoot = false) => {
      const slide = cloneMobileSlide(isRoot)
      if (!slide) return null

      slide.dataset.panelId = panelId

      const panel = clonePanel(panelId)
      if (panel) slide.appendChild(panel)

      return slide
    }

    const clearDesktopColumns = (startIndex = 0) => {
      desktopColumns.slice(startIndex).forEach((column, offset) => {
        const index = startIndex + offset
        column.replaceChildren()
        if (index > 0) desktopShells[index].hidden = true
      })
    }

    const setActiveTrigger = (panel, activeTrigger) => {
      panel.querySelectorAll('[data-panel-target]').forEach((trigger) => {
        trigger.setAttribute('aria-expanded', trigger === activeTrigger ? 'true' : 'false')
      })
    }

    const renderDesktopPanel = (panelId, columnIndex) => {
      const column = desktopColumns[columnIndex]
      if (!column) return null

      const panel = clonePanel(panelId)
      if (!panel) {
        column.replaceChildren()
        if (columnIndex > 0) desktopShells[columnIndex].hidden = true
        return null
      }

      desktopShells[columnIndex].hidden = false
      column.replaceChildren(panel)
      return panel
    }

    const initializeDesktop = () => {
      if (!desktopColumns.length) return

      clearDesktopColumns(1)
      renderDesktopPanel(rootPanelId, 0)
    }

    const initializeMobile = () => {
      if (!mobileInner) return

      mobileStack = [rootPanelId]

      const rootSlide = createMobileSlide(rootPanelId, true)
      if (!rootSlide) {
        mobileInner.replaceChildren()
        return
      }

      mobileInner.replaceChildren(rootSlide)

      if (mobileCarouselEl && window.bootstrap?.Carousel) {
        mobileCarousel = window.bootstrap.Carousel.getOrCreateInstance(mobileCarouselEl, {
          interval: false,
          ride: false,
          touch: false,
          wrap: false
        })
        mobileCarousel.to(0)
      }
    }

    const pushMobilePanel = (panelId) => {
      if (!mobileInner || !mobileCarousel) return

      const newSlide = createMobileSlide(panelId)
      if (!newSlide) return

      mobileInner.appendChild(newSlide)
      mobileStack.push(panelId)
      mobileCarousel.to(mobileStack.length - 1)
    }

    const popMobilePanel = () => {
      if (!mobileCarouselEl || !mobileCarousel || !mobileInner || mobileStack.length <= 1) return

      const currentIndex = mobileStack.length - 1
      const currentSlide = mobileInner.children[currentIndex]
      if (!currentSlide) return

      const handleSlid = (event) => {
        if (event.to !== currentIndex - 1) return

        mobileCarouselEl.removeEventListener('slid.bs.carousel', handleSlid)
        currentSlide.remove()
        mobileStack.pop()
      }

      mobileCarouselEl.addEventListener('slid.bs.carousel', handleSlid)
      mobileCarousel.to(currentIndex - 1)
    }

    const resetPanels = () => {
      initializeDesktop()
      initializeMobile()
    }

    const handleClick = (event) => {
      const backButton = event.target.closest('[data-menu-back]')
      if (backButton) {
        event.preventDefault()
        popMobilePanel()
        return
      }

      const panelTrigger = event.target.closest('[data-panel-target]')
      if (!panelTrigger) return

      if (desktopMedia.matches) {
        return
      }

      event.preventDefault()
      pushMobilePanel(panelTrigger.dataset.panelTarget)
    }

    const handleMouseOver = (event) => {
      if (!desktopMedia.matches) return

      const menuItem = event.target.closest('[data-menu-item]')
      if (!menuItem || !drawer.contains(menuItem)) return
      if (event.relatedTarget && menuItem.contains(event.relatedTarget)) return

      const currentPanel = menuItem.closest('[data-menu-panel-instance]')
      const currentColumn = menuItem.closest('[data-menu-desktop-column]')
      if (!currentPanel || !currentColumn) return

      const columnIndex = Number(currentColumn.dataset.menuDesktopColumn)
      if (Number.isNaN(columnIndex)) return

      if (!menuItem.dataset.panelTarget) {
        setActiveTrigger(currentPanel, null)
        clearDesktopColumns(columnIndex + 1)
        return
      }

      setActiveTrigger(currentPanel, menuItem)
      clearDesktopColumns(columnIndex + 1)
      renderDesktopPanel(menuItem.dataset.panelTarget, columnIndex + 1)
    }

    const handleShow = () => {
      resetPanels()
    }

    const handleResize = () => {
      if (!drawer.classList.contains('show')) return

      if (desktopMedia.matches) initializeDesktop()
      else initializeMobile()
    }

    resetPanels()

    drawer.addEventListener('show.bs.offcanvas', handleShow)
    drawer.addEventListener('click', handleClick)
    drawer.addEventListener('mouseover', handleMouseOver)
    window.addEventListener('resize', handleResize)

    destroyFns.push(() => {
      drawer.removeEventListener('show.bs.offcanvas', handleShow)
      drawer.removeEventListener('click', handleClick)
      drawer.removeEventListener('mouseover', handleMouseOver)
      window.removeEventListener('resize', handleResize)
    })
  })

  destroyMenuController = () => {
    destroyFns.forEach((destroy) => destroy())
  }
}

const mountMargaritaTheme = () => {
  mountAnnouncementCarousel()
  mountMargaritaHeader()
  mountMargaritaMenuDrawer()
  mountIndexPopup()
  mountVariantCollectionMode()
  mountDelegatedProductCardEvents()
  mountProductCardVariantControllers()
}

mountMargaritaTheme()

document.addEventListener('shopify:section:load', () => {
  mountMargaritaTheme()
})
